import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  Organisation,
  OrganisationUser,
} from '../../interfaces/organisation.interface';
import { OrganisationOrderOverview, OrganisationUserOverview } from '../../interfaces/organisation-overview.interface';
import { OrganisationActions } from '../actions/organisation.actions';

export interface OrganisationState extends EntityState<OrganisationUser> {
  error: unknown;
  pending: boolean;
  organisation: Organisation | null;
  userOverview: OrganisationUserOverview | null;
  userOverviewPending: boolean;
  orderOverview: OrganisationOrderOverview | null;
  orderOverviewPending: boolean;
}


const adapter: EntityAdapter<OrganisationUser> =
  createEntityAdapter<OrganisationUser>({
    selectId: (user: OrganisationUser): string => user.userId,
  });

export const initialState: OrganisationState = adapter.getInitialState({
  error: null,
  pending: false,
  organisation: null,
  userOverview: null,
  userOverviewPending: false,
  orderOverview: null,
  orderOverviewPending: false
});

const OrganisationReducer = createReducer(
  initialState,
  on(OrganisationActions.clearState, (): OrganisationState => initialState),
  on(
    OrganisationActions.clearErrorState,
    (state): OrganisationState => ({
      ...state,
      error: null,
    }),
  ),
  on(
    OrganisationActions.getUserOverview,
    (state): OrganisationState => ({ ...state, userOverviewPending: true }),
  ),

  on(
    OrganisationActions.getUserOverviewSuccess,
    (state, { data }): OrganisationState => ({
      ...state,
      userOverview: data,
      userOverviewPending: false
    })
  ),
  on(
    OrganisationActions.getUserOverviewFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      userOverview: null,
      userOverviewPending: false,
    }),
  ),
  on(
    OrganisationActions.getOrderOverview,
    (state): OrganisationState => ({ ...state, orderOverviewPending: true }),
  ),
  on(
    OrganisationActions.getOrderOverviewSuccess,
    (state, { data }): OrganisationState => ({
      ...state,
      orderOverview: data,
      orderOverviewPending: false
    })
  ),
  on(
    OrganisationActions.getOrderOverviewFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      orderOverview: null,
      orderOverviewPending: false,
    }),
  ),
  on(
    OrganisationActions.removeUser,
    (state): OrganisationState => ({ ...state, pending: true }),
  ),
  on(
    OrganisationActions.removeUserSuccess,
    (state, { user }): OrganisationState =>
      adapter.removeOne(user.userId, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    OrganisationActions.removeUserFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    OrganisationActions.get,
    (state): OrganisationState => ({ ...state, pending: true }),
  ),
  on(
    OrganisationActions.getSuccess,
    (state, { data }): OrganisationState => ({
      ...state,
      organisation: data,
      error: null,
      pending: false,
    }),
  ),
  on(
    OrganisationActions.getFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    OrganisationActions.getAllUsers,
    (state): OrganisationState => ({ ...state, pending: true }),
  ),
  on(
    OrganisationActions.getAllUsersSuccess,
    (state, { data }): OrganisationState =>
      adapter.upsertMany(data, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    OrganisationActions.getAllUsersFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    OrganisationActions.getUser,
    (state): OrganisationState => ({ ...state, pending: true }),
  ),
  on(
    OrganisationActions.getUserSuccess,
    (state, { data }): OrganisationState =>
      adapter.upsertOne(data, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    OrganisationActions.getUserFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    OrganisationActions.getUsers,
    (state): OrganisationState => ({ ...state, pending: true }),
  ),
  on(
    OrganisationActions.getUsersSuccess,
    (state, { data }): OrganisationState =>
      adapter.setAll(data, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    OrganisationActions.getUsersSuccess,
    (state): OrganisationState => ({ ...state, pending: false }),
  ),
  on(
    OrganisationActions.getUsersFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    OrganisationActions.inviteResend,
    (state): OrganisationState => ({ ...state, pending: true }),
  ),
  on(
    OrganisationActions.inviteResendSuccess,
    (state, { user }): OrganisationState =>
      adapter.upsertOne(user, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    OrganisationActions.inviteResendFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    OrganisationActions.inviteUser,
    (state): OrganisationState => ({ ...state, pending: true, error: null }),
  ),
  on(
    OrganisationActions.inviteUserSuccess,
    (state, { user }): OrganisationState =>
      adapter.upsertOne(user, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    OrganisationActions.inviteUserFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(
    OrganisationActions.updateUserRole,
    (state): OrganisationState => ({ ...state, pending: true }),
  ),
  on(
    OrganisationActions.updateUserRoleSuccess,
    (state, { user }): OrganisationState =>
      adapter.upsertOne(user, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(
    OrganisationActions.updateUserRoleFailure,
    (state, { error }): OrganisationState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
);

const { selectAll } = adapter.getSelectors();

export const selectAllOrganisationUser = selectAll;

export const reducer = (state: OrganisationState | undefined, action: Action) =>
  OrganisationReducer(state, action);
